import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";

const TABS = [
  { id: "help",      label: "Help Center" },
  { id: "contact",   label: "Contact" },
  { id: "status",    label: "Status" },
  { id: "community", label: "Community" },
];

const ARTICLES = [
  {
    category: "Getting Started",
    items: [
      { q: "How do I create a MoneyMeet Pay account?", a: "Download the app or visit moneymeetpay.com, tap 'Open free account', and follow the onboarding steps. Verification takes under 3 minutes." },
      { q: "What documents do I need to verify my identity?", a: "A valid government-issued photo ID (passport, national ID, or driving licence). Some corridors may require a selfie for enhanced verification." },
      { q: "Which countries can I send money to?", a: "MoneyMeet Pay supports 45+ destination countries across Africa, South Asia, Southeast Asia, Latin America, and the Caribbean. The full corridor list is shown in the app." },
    ],
  },
  {
    category: "Transfers",
    items: [
      { q: "How long do transfers take?", a: "Most transfers arrive within minutes. Some corridors may take up to 24 hours depending on the local banking network and time of day." },
      { q: "What's the maximum I can send?", a: "Daily and monthly limits depend on your KYC tier. Verified personal accounts can send up to £5,000/day. Business accounts have higher limits." },
      { q: "Can I cancel a transfer?", a: "Transfers can be cancelled within 30 minutes if they haven't been picked up by the recipient. Contact support immediately if you need to cancel." },
    ],
  },
  {
    category: "Fees & Rates",
    items: [
      { q: "What fees does MoneyMeet Pay charge?", a: "A flat 0.5% on every transfer — nothing else. No hidden charges, no monthly fee, no minimum balance requirement." },
      { q: "What exchange rate do you use?", a: "We use the mid-market rate — the rate you see on Google or XE. We never inflate the exchange rate to hide fees." },
      { q: "Are there any receiving fees?", a: "No. Recipients pay nothing to receive money sent through MoneyMeet Pay." },
    ],
  },
  {
    category: "Security",
    items: [
      { q: "Is my money safe?", a: "We never use customer funds for lending or investment. [REQUIRED: confirm and state our actual licensing/regulatory status and how customer funds are safeguarded before this answer is published — do not claim FCA authorisation unless independently confirmed.]" },
      { q: "How do I report a suspicious transaction?", a: "Contact our fraud team immediately via the in-app chat or by emailing fraud@moneymeetpay.com. We operate 24/7 for fraud emergencies." },
    ],
  },
];

const STATUS_ITEMS = [
  { name: "Transfer processing",  status: "operational" },
  { name: "Mobile app (iOS)",     status: "operational" },
  { name: "Mobile app (Android)", status: "operational" },
  { name: "Web platform",         status: "operational" },
  { name: "FX rates feed",        status: "operational" },
  { name: "KYC verification",     status: "operational" },
  { name: "Notifications",        status: "operational" },
  { name: "API gateway",          status: "operational" },
];

const COMMUNITY_LINKS = [
  { icon: "💬", name: "Discord Server", desc: "Chat with the MoneyMeet community, get peer help, and share tips.", href: "#" },
  { icon: "𝕏",  name: "Twitter / X",   desc: "Follow @MoneyMeetPay for product updates and announcements.", href: "#" },
  { icon: "📘", name: "Facebook Group", desc: "Join thousands of members sharing remittance experiences.", href: "#" },
  { icon: "▶️", name: "YouTube Channel", desc: "Video guides, tutorials, and feature walkthroughs.", href: "#" },
];

function HelpCenter() {
  const [open, setOpen] = useState({});
  const [query, setQuery] = useState("");

  const filtered = ARTICLES.map((cat) => ({
    ...cat,
    items: cat.items.filter(
      (i) =>
        query === "" ||
        i.q.toLowerCase().includes(query.toLowerCase()) ||
        i.a.toLowerCase().includes(query.toLowerCase()),
    ),
  })).filter((cat) => cat.items.length > 0);

  return (
    <div>
      <div style={{ marginBottom: "32px" }}>
        <input
          type="text"
          placeholder="Search help articles…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            width: "100%", padding: "14px 20px",
            borderRadius: "12px", fontSize: "15px",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.10)",
            color: "white", outline: "none",
            fontFamily: "'Inter', system-ui, sans-serif",
          }}
          onFocus={(e) => { e.target.style.borderColor = "rgba(16,185,129,0.45)"; }}
          onBlur={(e)  => { e.target.style.borderColor = "rgba(255,255,255,0.10)"; }}
        />
      </div>
      {filtered.length === 0 && (
        <p style={{ color: "rgba(255,255,255,0.36)", fontSize: "14px" }}>
          No articles matched your search.
        </p>
      )}
      {filtered.map((cat) => (
        <div key={cat.category} style={{ marginBottom: "32px" }}>
          <p style={{
            fontSize: "10px", fontWeight: 700, letterSpacing: "0.18em",
            textTransform: "uppercase", color: "rgba(255,255,255,0.26)",
            marginBottom: "12px",
          }}>
            {cat.category}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
            {cat.items.map((item) => {
              const key = cat.category + item.q;
              const isOpen = open[key];
              return (
                <div
                  key={item.q}
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.07)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    marginBottom: "4px",
                  }}
                >
                  <button
                    onClick={() => setOpen((p) => ({ ...p, [key]: !isOpen }))}
                    style={{
                      width: "100%", display: "flex", alignItems: "center",
                      justifyContent: "space-between", gap: "12px",
                      padding: "16px 20px", background: "none", border: "none",
                      cursor: "pointer", textAlign: "left",
                    }}
                  >
                    <span style={{ color: "rgba(255,255,255,0.82)", fontSize: "14px", fontWeight: 500 }}>
                      {item.q}
                    </span>
                    <span style={{
                      color: "rgba(255,255,255,0.28)", fontSize: "18px", lineHeight: 1,
                      flexShrink: 0, transition: "transform 0.18s",
                      display: "inline-block",
                      transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                    }}>+</span>
                  </button>
                  {isOpen && (
                    <div style={{
                      padding: "0 20px 16px",
                      color: "rgba(255,255,255,0.52)", fontSize: "13px", lineHeight: 1.7,
                    }}>
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Contact() {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", topic: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    // There is no backend endpoint for support-ticket submission today —
    // rather than claim a message was "sent" when nothing left the browser,
    // hand it off to the user's own email client via a pre-filled mailto:
    // link, addressed to the real support inbox referenced elsewhere on
    // this page.
    const subject = `[Support] ${form.topic || "General enquiry"}`;
    const body = `From: ${form.name} <${form.email}>\n\n${form.message}`;
    window.location.href =
      `mailto:support@moneymeetpay.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  if (sent) {
    return (
      <div style={{
        textAlign: "center", padding: "48px 24px",
        background: "rgba(16,185,129,0.06)",
        border: "1px solid rgba(16,185,129,0.18)",
        borderRadius: "16px",
      }}>
        <div style={{ fontSize: "36px", marginBottom: "12px" }}>✓</div>
        <h3 style={{ color: "var(--color-success)", fontWeight: 700, fontSize: "18px", marginBottom: "8px" }}>
          Opening your email client…
        </h3>
        <p style={{ color: "rgba(255,255,255,0.48)", fontSize: "14px" }}>
          We've pre-filled a message to support@moneymeetpay.com with your details — just hit send in your mail app. If nothing opened, email us directly.
        </p>
        <button
          onClick={() => { setSent(false); setForm({ name: "", email: "", topic: "", message: "" }); }}
          style={{
            marginTop: "20px", padding: "10px 24px", borderRadius: "100px",
            background: "none", border: "1px solid rgba(255,255,255,0.14)",
            color: "rgba(255,255,255,0.52)", fontSize: "13px", cursor: "pointer",
          }}
        >
          Send another message
        </button>
      </div>
    );
  }

  const inputStyle = {
    width: "100%", padding: "13px 16px",
    borderRadius: "10px", fontSize: "14px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.10)",
    color: "white", outline: "none",
    fontFamily: "'Inter', system-ui, sans-serif",
    boxSizing: "border-box",
  };

  return (
    <div style={{ maxWidth: "560px" }}>
      <p style={{ color: "rgba(255,255,255,0.44)", fontSize: "14px", marginBottom: "28px", lineHeight: 1.65 }}>
        Our support team is available Monday–Friday, 8am–8pm GMT. For urgent fraud or security issues, we're available 24/7.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ fontSize: "11px", fontWeight: 600, color: "rgba(255,255,255,0.36)", letterSpacing: "0.04em" }}>
            FULL NAME
          </label>
          <input
            style={inputStyle}
            placeholder="Michael Adeyemi"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            onFocus={(e) => { e.target.style.borderColor = "rgba(16,185,129,0.45)"; }}
            onBlur={(e)  => { e.target.style.borderColor = "rgba(255,255,255,0.10)"; }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ fontSize: "11px", fontWeight: 600, color: "rgba(255,255,255,0.36)", letterSpacing: "0.04em" }}>
            EMAIL ADDRESS
          </label>
          <input
            type="email"
            style={inputStyle}
            placeholder="michael@example.com"
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            onFocus={(e) => { e.target.style.borderColor = "rgba(16,185,129,0.45)"; }}
            onBlur={(e)  => { e.target.style.borderColor = "rgba(255,255,255,0.10)"; }}
          />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "12px" }}>
        <label style={{ fontSize: "11px", fontWeight: 600, color: "rgba(255,255,255,0.36)", letterSpacing: "0.04em" }}>
          TOPIC
        </label>
        <select
          style={{ ...inputStyle, appearance: "none" }}
          value={form.topic}
          onChange={(e) => setForm((p) => ({ ...p, topic: e.target.value }))}
          onFocus={(e) => { e.target.style.borderColor = "rgba(16,185,129,0.45)"; }}
          onBlur={(e)  => { e.target.style.borderColor = "rgba(255,255,255,0.10)"; }}
        >
          <option value="">Select a topic…</option>
          <option value="transfer">Transfer issue</option>
          <option value="account">Account / verification</option>
          <option value="fees">Fees & rates</option>
          <option value="security">Security concern</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "24px" }}>
        <label style={{ fontSize: "11px", fontWeight: 600, color: "rgba(255,255,255,0.36)", letterSpacing: "0.04em" }}>
          MESSAGE
        </label>
        <textarea
          style={{ ...inputStyle, minHeight: "120px", resize: "vertical" }}
          placeholder="Describe your issue or question…"
          value={form.message}
          onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
          onFocus={(e) => { e.target.style.borderColor = "rgba(16,185,129,0.45)"; }}
          onBlur={(e)  => { e.target.style.borderColor = "rgba(255,255,255,0.10)"; }}
        />
      </div>

      <button
        onClick={handleSubmit}
        disabled={!form.name || !form.email || !form.topic || !form.message}
        style={{
          padding: "14px 32px", borderRadius: "100px",
          background: "linear-gradient(135deg, #10B981, #059669)",
          color: "white", fontWeight: 700, fontSize: "14px",
          border: "none", cursor: "pointer",
          opacity: (!form.name || !form.email || !form.topic || !form.message) ? 0.42 : 1,
          transition: "opacity 0.2s",
        }}
      >
        Send message
      </button>
    </div>
  );
}

function Status() {
  return (
    <div>
      <div style={{
        display: "flex", alignItems: "center", gap: "10px",
        padding: "16px 20px", borderRadius: "12px", marginBottom: "28px",
        background: "rgba(16,185,129,0.07)",
        border: "1px solid rgba(16,185,129,0.20)",
      }}>
        <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#10B981", flexShrink: 0 }} />
        <p style={{ color: "rgba(255,255,255,0.82)", fontSize: "14px", fontWeight: 600 }}>
          All systems operational
        </p>
        <span style={{ marginLeft: "auto", fontSize: "11px", color: "rgba(255,255,255,0.30)" }}>
          Updated just now
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {STATUS_ITEMS.map((item) => (
          <div
            key={item.name}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "14px 18px", borderRadius: "10px",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
              marginBottom: "4px",
            }}
          >
            <span style={{ color: "rgba(255,255,255,0.70)", fontSize: "13px" }}>{item.name}</span>
            <span style={{
              fontSize: "11px", fontWeight: 700, padding: "3px 10px",
              borderRadius: "20px",
              background: "rgba(16,185,129,0.10)",
              color: "#10B981",
              border: "1px solid rgba(16,185,129,0.18)",
            }}>
              Operational
            </span>
          </div>
        ))}
      </div>

      <p style={{ marginTop: "20px", fontSize: "12px", color: "rgba(255,255,255,0.24)" }}>
        Incident history and SLA reports are available to business customers in the dashboard.
      </p>
    </div>
  );
}

function Community() {
  return (
    <div>
      <p style={{ color: "rgba(255,255,255,0.44)", fontSize: "14px", lineHeight: 1.65, marginBottom: "28px" }}>
        Join the MoneyMeet Pay community. Share tips, ask questions, and connect with other members of the global diaspora.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "12px" }}>
        {COMMUNITY_LINKS.map((link) => (
          <a
            key={link.name}
            href={link.href}
            style={{
              display: "flex", alignItems: "flex-start", gap: "14px",
              padding: "20px", borderRadius: "14px",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              textDecoration: "none", transition: "border-color 0.18s, background 0.18s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.14)";
              e.currentTarget.style.background = "rgba(255,255,255,0.07)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
              e.currentTarget.style.background = "rgba(255,255,255,0.04)";
            }}
          >
            <span style={{ fontSize: "22px", flexShrink: 0 }}>{link.icon}</span>
            <div>
              <p style={{ color: "rgba(255,255,255,0.84)", fontWeight: 600, fontSize: "14px", marginBottom: "4px" }}>
                {link.name}
              </p>
              <p style={{ color: "rgba(255,255,255,0.38)", fontSize: "12px", lineHeight: 1.55 }}>
                {link.desc}
              </p>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

export default function Support() {
  const location = useLocation();
  const navigate = useNavigate();

  const hashTab = location.hash.replace("#", "") || "help";
  const activeTab = TABS.find((t) => t.id === hashTab) ? hashTab : "help";

  const sectionRefs = useRef({});

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = sectionRefs.current[id];
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash]);

  const setTab = (id) => {
    navigate(`/support#${id}`, { replace: true });
  };

  const SECTION_COMPONENTS = {
    help:      <HelpCenter />,
    contact:   <Contact />,
    status:    <Status />,
    community: <Community />,
  };

  return (
    <div style={{ minHeight: "100vh", background: "hsl(var(--mm-void))", fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* Header */}
      <header style={{
        position: "sticky", top: 0, zIndex: 50,
        background: "rgba(3,9,16,0.88)", backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(255,255,255,0.07)",
        padding: "0 24px",
      }}>
        <div style={{
          maxWidth: "1000px", margin: "0 auto",
          display: "flex", alignItems: "center", gap: "12px",
          height: "60px",
        }}>
          <Link
            to="/"
            style={{
              display: "flex", alignItems: "center", gap: "8px",
              color: "rgba(255,255,255,0.52)", fontSize: "13px", textDecoration: "none",
              transition: "color 0.18s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.82)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.52)"; }}
          >
            ← Back
          </Link>
          <span style={{ color: "rgba(255,255,255,0.14)", fontSize: "14px" }}>|</span>
          <span style={{ color: "rgba(255,255,255,0.60)", fontSize: "14px", fontWeight: 600 }}>Support</span>
        </div>
      </header>

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "48px 24px 80px" }}>

        {/* Hero */}
        <div style={{ marginBottom: "40px" }}>
          <h1 style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontWeight: 900, color: "white",
            fontSize: "clamp(2rem, 4vw, 3.2rem)",
            letterSpacing: "-0.04em", marginBottom: "10px",
          }}>
            How can we help?
          </h1>
          <p style={{ color: "rgba(255,255,255,0.40)", fontSize: "15px" }}>
            Support for MoneyMeet Pay customers and businesses.
          </p>
        </div>

        {/* Tab bar */}
        <div style={{
          display: "flex", gap: "4px", marginBottom: "36px",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "12px", padding: "4px",
          width: "fit-content",
        }}>
          {TABS.map((tab) => {
            const active = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                onClick={() => setTab(tab.id)}
                style={{
                  padding: "9px 20px", borderRadius: "9px",
                  fontSize: "13px", fontWeight: 600,
                  border: "none", cursor: "pointer",
                  transition: "all 0.18s",
                  background: active ? "rgba(255,255,255,0.10)" : "transparent",
                  color: active ? "white" : "rgba(255,255,255,0.42)",
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Active section */}
        <div>
          {SECTION_COMPONENTS[activeTab]}
        </div>

      </div>
    </div>
  );
}
